import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { isAdminOrSuperAdmin } from 'features/auth/helpers';
import { clearUserSummaryCache } from 'features/salesOrder/userSummaryCache';
import { SALES_TEAMS } from 'utils/slug';
import { getSellerTeams, getSellers, updateSellerTeamGoal } from '../../services';
import { ISeller } from '../../types';
import {
	SellerForm,
	describeSellerWriteError,
	formToTeamGoal,
	formatGoal,
	isDashboardTeam,
	sameTeamGoal,
	sellerToForm,
	validateGoal,
} from '../../helpers';

const NO_TEAM = '__sin_equipo__';
const ALL_TEAMS = '';

interface DialogProps {
	seller: ISeller;
	teams: string[];
	onClose: () => void;
	onSaved: (seller: ISeller) => void;
}

/** Editar el equipo y el objetivo de un vendedor. Se monta al abrirse: arranca con lo de ese vendedor. */
function SellerDialog({ seller, teams, onClose, onSaved }: DialogProps): JSX.Element {
	const [form, setForm] = useState<SellerForm>(() => sellerToForm(seller));
	const [saving, setSaving] = useState(false);
	const [apiError, setApiError] = useState<string | null>(null);
	const goalError = validateGoal(form.goal);
	const team = form.team.trim();

	const handleSave = async () => {
		if (goalError) return;
		const body = formToTeamGoal(form);
		// Sin cambios no se llama a la API. Se compara con lo guardado tal cual: un equipo mal escrito (`centro uy`) se
		// corrige con solo guardar.
		if (sameTeamGoal(body, { team: seller.team ?? null, goal: seller.goal ?? null })) {
			onClose();
			return;
		}
		setSaving(true);
		setApiError(null);
		try {
			const { data } = await updateSellerTeamGoal(seller.id, body);
			// La API no manda la clave de lo que quedó vacío: sin esto, la fila seguiría mostrando lo de antes.
			onSaved({ ...seller, ...data, team: data.team ?? null, goal: data.goal ?? null });
		} catch (error) {
			setApiError(describeSellerWriteError(error));
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
			<DialogTitle>Equipo y objetivo de {seller.name}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<Autocomplete
						freeSolo
						options={teams}
						value={form.team}
						onInputChange={(_, value) => setForm((current) => ({ ...current, team: value }))}
						disabled={saving}
						renderInput={(params) => (
							<TextField {...params} size="small" label="Equipo" helperText="Vacío: sin equipo" />
						)}
					/>
					{team !== '' && !isDashboardTeam(team) && (
						<Alert severity="info">
							Los dashboards de ventas reconocen {SALES_TEAMS.join(', ')}: con otro equipo, este usuario no aparece en
							ellos.
						</Alert>
					)}
					<TextField
						size="small"
						label="Objetivo mensual (margen bruto)"
						value={form.goal}
						onChange={(event) => setForm((current) => ({ ...current, goal: event.target.value }))}
						disabled={saving}
						inputProps={{ inputMode: 'numeric' }}
						InputProps={{ startAdornment: <InputAdornment position="start">USD</InputAdornment> }}
						error={goalError !== null}
						helperText={goalError ?? 'Vacío: sin objetivo'}
					/>
					{apiError && <Alert severity="error">{apiError}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={saving || goalError !== null}
					startIcon={saving ? <CircularProgress size={16} /> : undefined}
				>
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
}

/**
 * Los usuarios del Power con su equipo y su objetivo mensual (los de los dashboards de ventas). Un administrador los
 * cambia acá; antes se cargaban a mano en la base.
 */
export function SellersTeamGoalList(): JSX.Element {
	const canEdit = isAdminOrSuperAdmin(useSelector((state: RootState) => state.auth.user));
	const [sellers, setSellers] = useState<ISeller[]>([]);
	const [teams, setTeams] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [teamFilter, setTeamFilter] = useState(ALL_TEAMS);
	const [onlyActive, setOnlyActive] = useState(true);
	const [editing, setEditing] = useState<ISeller | null>(null);
	const [saved, setSaved] = useState<string | null>(null);

	useEffect(() => {
		let alive = true;
		getSellers()
			.then(({ data }) => {
				if (alive) setSellers(data);
			})
			.catch(() => {
				if (alive) setLoadError('No se pudieron cargar los usuarios.');
			})
			.finally(() => {
				if (alive) setLoading(false);
			});
		// La API vieja no tiene `/teams`: los equipos salen de la lista.
		getSellerTeams()
			.then(({ data }) => {
				if (alive) setTeams(data);
			})
			.catch(() => undefined);
		return () => {
			alive = false;
		};
	}, []);

	const teamOptions = useMemo(() => {
		const fromList = sellers.map((seller) => seller.team?.trim() ?? '').filter((team) => team !== '');
		return Array.from(new Set([...teams, ...fromList])).sort();
	}, [teams, sellers]);

	const visible = useMemo(() => {
		const text = search.trim().toLowerCase();
		return sellers
			.filter((seller) => !onlyActive || seller.active)
			.filter((seller) =>
				teamFilter === ALL_TEAMS
					? true
					: teamFilter === NO_TEAM
					? !seller.team?.trim()
					: seller.team?.trim() === teamFilter,
			)
			.filter(
				(seller) =>
					text === '' ||
					[seller.name, seller.username, seller.email].some((value) => value.toLowerCase().includes(text)),
			)
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [sellers, search, teamFilter, onlyActive]);

	const handleSaved = (updated: ISeller) => {
		setSellers((current) => current.map((seller) => (seller.id === updated.id ? updated : seller)));
		// El equipo del usuario se guarda en caché para el Home y los dashboards: que lo vuelvan a leer.
		clearUserSummaryCache();
		setEditing(null);
		setSaved(`Se guardó el equipo y el objetivo de ${updated.name}`);
	};

	if (loading) return <CircularProgress aria-label="Cargando los usuarios" />;
	if (loadError) return <Alert severity="error">{loadError}</Alert>;

	return (
		<Stack spacing={2}>
			<Paper sx={{ p: 2 }}>
				<Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
					<TextField
						size="small"
						label="Buscar"
						placeholder="Nombre, usuario o mail"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
					/>
					<FormControl size="small" sx={{ minWidth: 200 }}>
						<InputLabel id="sellers-team-filter">Equipo</InputLabel>
						<Select
							labelId="sellers-team-filter"
							label="Equipo"
							value={teamFilter}
							onChange={(event) => setTeamFilter(event.target.value)}
						>
							<MenuItem value={ALL_TEAMS}>
								<em>Todos</em>
							</MenuItem>
							<MenuItem value={NO_TEAM}>Sin equipo</MenuItem>
							{teamOptions.map((team) => (
								<MenuItem key={team} value={team}>
									{team}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControlLabel
						control={<Switch checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} />}
						label="Solo activos"
					/>
					<Typography variant="body2" color="text.secondary">
						{visible.length} {visible.length === 1 ? 'usuario' : 'usuarios'}
					</Typography>
				</Box>
			</Paper>

			<TableContainer component={Paper}>
				<Table size="small" aria-label="Vendedores: equipo y objetivo">
					<TableHead>
						<TableRow>
							<TableCell>Nombre</TableCell>
							<TableCell>Usuario</TableCell>
							<TableCell>Equipo</TableCell>
							<TableCell align="right">Objetivo mensual</TableCell>
							<TableCell>Estado</TableCell>
							{canEdit && <TableCell align="center">Acciones</TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{visible.length === 0 ? (
							<TableRow>
								<TableCell colSpan={canEdit ? 6 : 5} align="center">
									No hay usuarios con esos filtros.
								</TableCell>
							</TableRow>
						) : (
							visible.map((seller) => (
								<TableRow key={seller.id} hover>
									<TableCell>{seller.name}</TableCell>
									<TableCell>{seller.username}</TableCell>
									<TableCell>{seller.team?.trim() || '—'}</TableCell>
									<TableCell align="right">{formatGoal(seller.goal)}</TableCell>
									<TableCell>{seller.active ? 'Activo' : 'Inactivo'}</TableCell>
									{canEdit && (
										<TableCell align="center">
											<Button
												size="small"
												startIcon={<EditIcon />}
												onClick={() => setEditing(seller)}
												aria-label={`Editar el equipo y el objetivo de ${seller.name}`}
											>
												Editar
											</Button>
										</TableCell>
									)}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{editing && (
				<SellerDialog seller={editing} teams={teamOptions} onClose={() => setEditing(null)} onSaved={handleSaved} />
			)}

			<Snackbar
				open={saved !== null}
				autoHideDuration={6000}
				onClose={() => setSaved(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			>
				<Alert onClose={() => setSaved(null)} severity="success">
					{saved}
				</Alert>
			</Snackbar>
		</Stack>
	);
}
